import { useState } from "react";
import toast from "react-hot-toast";
import { AlertTriangle, Search, PackageX, Download, FileSpreadsheet } from "lucide-react";
import { useProducts, useBranches, useRegisterShrinkage, SHRINKAGE_REASON_LABEL, ShrinkageReason } from "@/api/inventory";
import { useShrinkageReport, downloadReportCsv } from "@/api/reports";
import { getApiErrorMessage } from "@/api/client";

const currency = new Intl.NumberFormat("es-CO", { style: "currency", currency: "COP", maximumFractionDigits: 0 });

function formatDateTime(iso: string): string {
  return new Date(iso).toLocaleString("es-CO", { dateStyle: "medium", timeStyle: "short" });
}

function ExportButtons({ endpoint, filename, params }: { endpoint: string; filename: string; params?: Record<string, string | number | undefined> }) {
  const [exporting, setExporting] = useState<"csv" | "xlsx" | null>(null);
  const handleExport = async (format: "csv" | "xlsx") => {
    setExporting(format);
    try {
      await downloadReportCsv(`${endpoint}.${format}`, `${filename}.${format}`, params);
    } catch (error) {
      toast.error(getApiErrorMessage(error));
    } finally {
      setExporting(null);
    }
  };
  return (
    <div className="flex shrink-0 gap-2">
      <button type="button" className="btn-secondary text-xs" disabled={exporting !== null} onClick={() => handleExport("xlsx")}>
        <FileSpreadsheet size={13} className="mr-1" /> Excel
      </button>
      <button type="button" className="btn-secondary text-xs" disabled={exporting !== null} onClick={() => handleExport("csv")}>
        <Download size={13} className="mr-1" /> CSV
      </button>
    </div>
  );
}

const REASON_OPTIONS = Object.entries(SHRINKAGE_REASON_LABEL) as Array<[ShrinkageReason, string]>;

/**
 * Mermas: registrar una baja de inventario por vencimiento, daño, robo,
 * error de conteo o derrame (con motivo categorizado), y ver el reporte
 * agregado por motivo/producto. Reutiliza `adjustStock` en el backend (el
 * único punto de escritura de stock) — no crea una tabla paralela, ver
 * inventory.service.ts::registerShrinkage.
 */
export default function ShrinkagePage() {
  const [search, setSearch] = useState("");
  const [productId, setProductId] = useState<string | null>(null);
  const [productName, setProductName] = useState("");
  const [branchId, setBranchId] = useState("");
  const [quantity, setQuantity] = useState("");
  const [reason, setReason] = useState<ShrinkageReason>("VENCIDO");
  const [notes, setNotes] = useState("");

  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const { data: productsResponse } = useProducts({ search: search || undefined, pageSize: 8, isActive: true });
  const products = productsResponse?.data ?? [];
  const { data: branches } = useBranches(true);
  const registerShrinkage = useRegisterShrinkage();
  const { data: report, isLoading: loadingReport } = useShrinkageReport({ dateFrom: dateFrom || undefined, dateTo: dateTo || undefined });

  const resetForm = () => {
    setSearch("");
    setProductId(null);
    setProductName("");
    setQuantity("");
    setReason("VENCIDO");
    setNotes("");
  };

  const handleSubmit = async () => {
    if (!productId) {
      toast.error("Selecciona un producto.");
      return;
    }
    const qty = Number(quantity);
    if (!qty || qty <= 0) {
      toast.error("Ingresa una cantidad mayor a cero.");
      return;
    }
    try {
      await registerShrinkage.mutateAsync({ productId, branchId: branchId || undefined, quantity: qty, reason, notes: notes || undefined });
      toast.success("Merma registrada.");
      resetForm();
    } catch (err) {
      toast.error(getApiErrorMessage(err));
    }
  };

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-xl font-semibold text-gray-900">Mermas</h1>
        <p className="text-sm text-gray-500">Registra bajas de inventario por vencimiento, daño, robo, error de conteo o derrame, con motivo categorizado.</p>
      </div>

      <div className="card space-y-3 p-4">
        <p className="text-sm font-semibold text-gray-900">Registrar merma</p>

        <div className="relative">
          <Search size={16} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            className="input-field pl-9"
            placeholder="Buscar producto por nombre, SKU o código de barras..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setProductId(null);
            }}
          />
        </div>
        {search && !productId && (
          <div className="max-h-56 divide-y divide-gray-100 overflow-y-auto rounded-md border border-gray-100">
            {products.map((p) => (
              <button
                key={p.id}
                type="button"
                className="flex w-full items-center justify-between px-3 py-2 text-left text-sm hover:bg-gray-50"
                onClick={() => {
                  setProductId(p.id);
                  setProductName(`${p.sku} — ${p.name}`);
                  setSearch(`${p.sku} — ${p.name}`);
                }}
              >
                <span className="text-gray-900">{p.name}</span>
                <span className="text-xs text-gray-400">{p.sku} · stock {p.stockQty}</span>
              </button>
            ))}
            {products.length === 0 && <p className="px-3 py-2 text-sm text-gray-400">Sin resultados.</p>}
          </div>
        )}

        {productId && (
          <div className="space-y-3 border-t border-gray-100 pt-3">
            <p className="text-sm text-gray-600">
              Producto: <span className="font-medium text-gray-900">{productName}</span>
            </p>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-4">
              <div>
                <label className="text-xs font-medium text-gray-500">Sucursal</label>
                <select className="input-field mt-1" value={branchId} onChange={(e) => setBranchId(e.target.value)}>
                  <option value="">Principal</option>
                  {branches?.map((b) => (
                    <option key={b.id} value={b.id}>
                      {b.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-xs font-medium text-gray-500">Cantidad perdida</label>
                <input type="number" min="0" step="0.01" className="input-field mt-1" value={quantity} onChange={(e) => setQuantity(e.target.value)} />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-500">Motivo</label>
                <select className="input-field mt-1" value={reason} onChange={(e) => setReason(e.target.value as ShrinkageReason)}>
                  {REASON_OPTIONS.map(([value, label]) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="sm:col-span-1">
                <label className="text-xs font-medium text-gray-500">Notas (opcional)</label>
                <input className="input-field mt-1" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Detalle adicional..." />
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <button type="button" className="btn-secondary" onClick={resetForm}>
                Cancelar
              </button>
              <button type="button" className="btn-primary" disabled={registerShrinkage.isPending} onClick={handleSubmit}>
                <AlertTriangle size={15} className="mr-1.5" /> Registrar merma
              </button>
            </div>
          </div>
        )}
      </div>

      <div className="card space-y-3 p-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <p className="text-sm font-semibold text-gray-900">Reporte de mermas</p>
          <div className="flex flex-wrap items-center gap-2 text-sm text-gray-600">
            <span>Desde</span>
            <input type="date" className="input-field w-auto" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
            <span>hasta</span>
            <input type="date" className="input-field w-auto" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
            <ExportButtons endpoint="/reports/store-shrinkage" filename="mermas" params={{ dateFrom: dateFrom || undefined, dateTo: dateTo || undefined }} />
          </div>
        </div>

        {report && (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <div className="rounded-lg bg-gray-50 p-3">
              <p className="text-xs uppercase tracking-wide text-gray-400">Unidades perdidas</p>
              <p className="text-xl font-bold text-gray-900">{report.totalQuantity}</p>
            </div>
            <div className="rounded-lg bg-gray-50 p-3">
              <p className="text-xs uppercase tracking-wide text-gray-400">Valor estimado</p>
              <p className="text-xl font-bold text-red-600">{currency.format(report.totalCostEstimate)}</p>
            </div>
            <div className="rounded-lg bg-gray-50 p-3">
              <p className="text-xs uppercase tracking-wide text-gray-400">Movimientos</p>
              <p className="text-xl font-bold text-gray-900">{report.movementsCount}</p>
            </div>
          </div>
        )}

        {report && report.byReason.length > 0 && (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <p className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-gray-400">Por motivo</p>
              <div className="divide-y divide-gray-100 rounded-md border border-gray-100">
                {report.byReason.map((r) => (
                  <div key={r.reason} className="flex items-center justify-between px-3 py-2 text-sm">
                    <span className="text-gray-700">{SHRINKAGE_REASON_LABEL[r.reason as ShrinkageReason] ?? r.reason}</span>
                    <span className="text-gray-500">{r.quantity} u.</span>
                    <span className="font-medium text-gray-900">{currency.format(r.costEstimate)}</span>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <p className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-gray-400">Por producto</p>
              <div className="divide-y divide-gray-100 rounded-md border border-gray-100">
                {report.byProduct.slice(0, 8).map((p) => (
                  <div key={p.productId} className="flex items-center justify-between px-3 py-2 text-sm">
                    <span className="truncate text-gray-700">{p.productName}</span>
                    <span className="shrink-0 text-gray-500">{p.quantity} u.</span>
                    <span className="shrink-0 font-medium text-gray-900">{currency.format(p.costEstimate)}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-left text-xs uppercase text-gray-500">
            <tr>
              <th className="px-3 py-2.5">Fecha</th>
              <th className="px-3 py-2.5">Producto</th>
              <th className="px-3 py-2.5">Sucursal</th>
              <th className="px-3 py-2.5">Motivo</th>
              <th className="px-3 py-2.5 text-right">Cantidad</th>
              <th className="px-3 py-2.5 text-right">Valor est.</th>
              <th className="px-3 py-2.5">Registrado por</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {loadingReport && (
              <tr>
                <td colSpan={7} className="px-3 py-6 text-center text-gray-400">
                  Cargando...
                </td>
              </tr>
            )}
            {report?.rows.map((r) => (
              <tr key={r.id}>
                <td className="px-3 py-2 text-gray-600">{formatDateTime(r.createdAt)}</td>
                <td className="px-3 py-2 text-gray-900">{r.productName}</td>
                <td className="px-3 py-2 text-gray-600">{r.branchName}</td>
                <td className="px-3 py-2 text-gray-600">{SHRINKAGE_REASON_LABEL[r.reason as ShrinkageReason] ?? r.reason}</td>
                <td className="px-3 py-2 text-right text-red-600">-{r.quantity}</td>
                <td className="px-3 py-2 text-right text-gray-900">{currency.format(r.costEstimate)}</td>
                <td className="px-3 py-2 text-gray-500">{r.createdByName ?? "—"}</td>
              </tr>
            ))}
            {report && report.rows.length === 0 && !loadingReport && (
              <tr>
                <td colSpan={7} className="px-3 py-8 text-center text-gray-400">
                  <PackageX size={22} className="mx-auto mb-2 text-gray-300" />
                  Sin mermas registradas en este rango.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
